import { ref, subscribe } from "valtio";
import { subscribeKey } from "valtio/utils";

import {
  IS_DEV,
  isPersistent,
  isPopulatedFromDB,
  isVolatile,
  PATH_DELIMITER,
} from "@/__main__/constants.mjs";
import dbObj, {
  __PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE,
} from "@/__main__/db.mjs";
import initialState, {
  applyIsInitial,
  freshInitialState,
} from "@/__main__/initial-state.mjs";
import mainRefs from "@/__main__/refs.mjs";
import checkSettings from "@/util/check-settings.mjs";
import clone from "@/util/clone.mjs";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import isObject from "@/util/is-object.mjs";
import { proxy } from "@/util/proxy.mjs";
import writeKeyPath, { CONFIG, keyPaths } from "@/util/state-cleanup.mjs";

// Interface to allow features to extend AppState for proper typing
export interface FeatureState {}

// Interface to allow typing volatile state
export interface VolatileState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type InitialState = typeof initialState;

// Interface to allow typing initial app state (without using `as T` in initial-state).
//
// The typings must be compatible with `initialState` and this is guaranteed by Typescript.
export interface AppState extends InitialState, FeatureState {}

export type DigAppState<P extends readonly PropertyKey[]> = DigValue<
  P,
  AppState
>;

// Used primarily to replace db with a mock for unit testing.
export const dbRef = {
  db: dbObj[__PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE],
};

// Exposing the internal db write promise in a nice way.
// Use this if it's needed to await for a write to be committed.
export async function writePromise() {
  await dbRef.db._init;
  await new Promise<void>(queueMicrotask);
  return dbRef.db._setManyPromise;
}

// How this module works is there are two objects exported:
//
// - readState
// - writeState
//
// The names should be self explanatory. One should use `readState` for reading,
// and `writeState` for writing. DO NOT WRITE TO READSTATE, only use writeState.
//
// There are a few reasons:
// - writeState is completely safe to write on any arbitrary key.
// - writeState keeps track of nesting and persists data at that location.
export const readState = proxy(initialState) as AppState;

// Used mainly in integration testing.
export function resetState() {
  const nextState = clone(freshInitialState);
  applyIsInitial(nextState);
  Object.assign(readState, nextState);
  mainRefs.recentURLs.clear();
  keyPaths.clear();
}

// This implementation is short but intricate. This proxy keeps track of
// access to its properties to build a path, then intercepts when it is written.
const readStack: PropertyKey[] = [];
const writerObj: AppState = {} as AppState;
const writeHandler = {
  get(_target: AppState, key: PropertyKey, receiver: unknown) {
    if (typeof key !== "string") {
      throw new TypeError(
        `Can not use non-string key to write state, ` +
          `because it complicates path serialization. Key: ${String(key)}`,
      );
    }

    readStack.push(key);
    return receiver;
  },
  set: writeProperty,
  deleteProperty: writeProperty,
};

function writeProperty<K extends PropertyKey>(
  _target: AppState,
  key: K,
  value?: unknown | Error /*, receiver*/,
) {
  // Signal that we're about to write.
  mainRefs.allowWrite();

  const keys: PropertyKey[] = readStack.slice();
  readStack.splice(0, readStack.length);
  const lastKey: PropertyKey = key;

  let curr: object = readState;

  for (const key of keys) {
    if (!curr.hasOwnProperty(key)) curr[key] = {};
    curr = curr[key];
  }

  const shouldPersist =
    value &&
    !(value instanceof Error) &&
    value[isPersistent] &&
    !(value && (value[isVolatile] || value[isPopulatedFromDB]));

  const keyPath = [...keys, lastKey];

  // Only write the keyPath if it was persistent or a deletion.
  if (value === undefined || (isObject(value) && value[isPersistent])) {
    writeKeyPath(keyPath, value === undefined);
  }

  // Commit the write to readState.
  if (value !== undefined) {
    // https://github.com/pmndrs/valtio#holding-objects-in-state-without-tracking-them
    if (isObject(value) && shouldPersist) {
      curr[lastKey] = ref(value);
    } else {
      curr[lastKey] = value;
    }
  } else {
    Reflect.deleteProperty(curr, lastKey);
  }

  const isValuePrimitive = value === null || typeof value !== "object";

  const shouldShowWarning =
    value &&
    !(value instanceof Error) &&
    !(value instanceof Date) &&
    !value[isPopulatedFromDB] &&
    !value[isPersistent] &&
    !value[isVolatile] &&
    !isValuePrimitive;

  if (shouldShowWarning) {
    devWarn(
      `WARNING: the path "${keyPath.join(".")}" was written without ` +
        `persisting. Please use the "isPersistent" symbol, or use "isVolatile" ` +
        `to get rid of this warning.`,
    );
  }

  // This is only relevant when writing once.
  if (isObject(value) && value[isPopulatedFromDB]) {
    delete value[isPopulatedFromDB];
  }

  if (shouldPersist || value === undefined) {
    if (value !== undefined) addRecentlyPersistedPath(keyPath);

    (async () => {
      const id = keyPath.join(PATH_DELIMITER);

      if (value !== undefined) {
        await dbRef.db.upsert([[id, value]]);
      } else {
        await dbRef.db.deleteMany([id]);
      }
    })().catch((error) => {
      devError(`Failed to write path "${keyPath.join(".")}"`, error);
    });
  } else {
    // It should check regardless of whether or not the current keyPath is
    // volatile, to prevent volatile objects from overwriting persistent objects.
    checkRecentlyPersistedPath(keyPath);
  }

  return true;
}

const PATH_EXPLANATION = `
We want to guarantee that the paths we write to are conflict-free. Consider this data structure:

{
  a: {
    b: {
      c: 1,
      d: 2,
      [isPersistent]: true,
    },
  },
}

In this example, the object "b" is the only object that is persisted. What could go wrong if we write to an invalid path?

- If object "a" is written, then we may have conflicts with the data that already exists in object "b".
- If key "c" or "d" is written to directly, then we have mutated data from object "b" without persisting.

Therefore, the only valid path to write to is "a.b".
`;

// This array is ever-increasing, if it ever exceeds 4,294,967,295 elements it will krash 🤡
export const recentlyPersistedPaths: Array<readonly PropertyKey[]> = [];
export const MAX_RECENT_PATHS = 50;

// We will constrain it to an arbitrarily smaller max length to be safe.
const RING_BUFFER_LENGTH = MAX_RECENT_PATHS * 10;

function checkRecentlyPersistedPath(keyPath: readonly PropertyKey[]) {
  let conflictingPath;
  for (
    let i = Math.max(0, recentlyPersistedPaths.length - MAX_RECENT_PATHS);
    i < recentlyPersistedPaths.length;
    i++
  ) {
    const p = recentlyPersistedPaths[i];
    if (!p) continue;

    const hasShallower =
      p.length < keyPath.length && p.every((part, i) => keyPath[i] === part);
    const hasDeeper =
      p.length > keyPath.length && keyPath.every((part, i) => p[i] === part);

    if (hasShallower || hasDeeper) conflictingPath = p;
  }
  if (conflictingPath) {
    const isShallow = conflictingPath.length < keyPath.length;
    throw new Error(
      isShallow
        ? `The path "${keyPath.join(
            PATH_DELIMITER,
          )}" was written to, but there was a shallower path ` +
          `"${conflictingPath.join(
            PATH_DELIMITER,
          )}" which was written to earlier. Refusing to write ` +
          `inside a nested path.\n${PATH_EXPLANATION}`
        : `The path "${keyPath.join(
            PATH_DELIMITER,
          )}" was written to, but there was a deeper path ` +
          `"${conflictingPath.join(
            PATH_DELIMITER,
          )}" which was written to earlier. Refusing to write ` +
          `a path that is shallower.\n${PATH_EXPLANATION}`,
    );
  }
}

export function addRecentlyPersistedPath(keyPath: readonly PropertyKey[]) {
  checkRecentlyPersistedPath(keyPath);

  recentlyPersistedPaths.push(keyPath);

  if (recentlyPersistedPaths.length > MAX_RECENT_PATHS) {
    delete recentlyPersistedPaths[
      recentlyPersistedPaths.length - MAX_RECENT_PATHS - 1
    ];
  }
  // Wrap around the ring buffer.
  if (recentlyPersistedPaths.length > RING_BUFFER_LENGTH) {
    for (
      let i = recentlyPersistedPaths.length - MAX_RECENT_PATHS, k = 0;
      k < MAX_RECENT_PATHS;
      i++, k++
    ) {
      recentlyPersistedPaths[k] = recentlyPersistedPaths[i];
    }
    recentlyPersistedPaths.length = MAX_RECENT_PATHS;
  }
}

const writeState = new Proxy(writerObj, writeHandler);

export const __ONLY_WRITE_STATE_FROM_ACTIONS = writeState;

// Configure state cleanup.
CONFIG.STATE = readState;

// DEBUG
globals.__BLITZ_DEV__.readState = readState;
globals.__BLITZ_DEV__.writeState = writeState;
globals.__BLITZ_DEV__.recentlyPersistedPaths = recentlyPersistedPaths;

if (IS_DEV) {
  subscribeKey(readState, "settings", checkSettings);
  subscribe(readState, function (changes) {
    for (const change of changes) {
      const [op, path, value] = change;
      if (op !== "set") continue;
      let keyPath;
      try {
        keyPath = path.join(PATH_DELIMITER);
      } catch (e) {
        devError("FAILED TO SERIALIZE PATH", path);
        return;
      }
      let hasMatch = false;
      for (const k of [...keyPaths, ...CONFIG.INITIAL_PATHS]) {
        if (k.startsWith(keyPath)) {
          hasMatch = true;
          break;
        }
      }
      if (!hasMatch) {
        devDebug(`Volatile state "${path.join(".")}" was written as`, value);
      }
      if (!mainRefs.writeSemaphore) {
        devWarn(
          `Path "${path.join(".")}" was written without using writeState!`,
        );
      }
    }
  });
}

// TESTING
export { isPersistent, isPopulatedFromDB, isVolatile };
